import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import iconShapes from '../../assets/method-icons/iconShapes.json';
import { startIconDraw } from './iconDraw';

// One of the 5 family icons (see src/assets/method-icons/iconShapes.json),
// drawn stroke-by-stroke with a traveling sparkle tip, then filled by a
// bouncing color "droplet" — starts immediately on mount, then loops
// forever, slowly. Vanilla JS/CSS engine lives in iconDraw.js (same
// conventions as src/components/home/cardMagicReveal.js — no library).
//
// Each shape carries a `role` (ink/paper/accent/line) captured from the
// source SVG's own light/dark relationships (see the extraction script in
// git history) — the fill phase paints by role, not one flat color,
// otherwise shapes designed as light cutouts against a dark body (or pure
// line-work meant to stay unfilled) collapse into an illegible solid blob.
export default function AnimatedIcon({ name, color, size = 96, startDelayMs = 0 }) {
  const theme = useTheme();
  const groupRef = useRef(null);
  const tipRef = useRef(null);
  const burstHostRef = useRef(null);

  useEffect(() => {
    const groupEl = groupRef.current;
    if (!groupEl) return undefined;
    const shapeEls = Array.from(groupEl.children).filter((el) => el !== tipRef.current);
    return startIconDraw({
      shapeEls, groupEl, tipEl: tipRef.current, burstHostEl: burstHostRef.current,
      color, inkColor: theme.home.ink, paperColor: theme.home.paper, startDelayMs,
    });
  }, [color, theme, startDelayMs]);

  const icon = iconShapes[name];
  if (!icon) return null;
  const vbH = Number(icon.viewBox.split(' ')[3]);
  const vbW = Number(icon.viewBox.split(' ')[2]);

  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size * (vbH / vbW) }}>
      <svg viewBox={icon.viewBox} width="100%" height="100%" aria-hidden="true">
        <g ref={groupRef} className="icon-draw-group">
          {icon.shapes.map((s, i) => (
            s.type === 'path'
              ? <path key={i} d={s.d} data-role={s.role} />
              : <circle key={i} cx={s.cx} cy={s.cy} r={s.r} data-role={s.role} />
          ))}
          <circle ref={tipRef} className="icon-draw-tip" cx="0" cy="0" r="4" />
        </g>
      </svg>
      <span ref={burstHostRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
    </span>
  );
}
